/*
 * trautis-wheel-fitment.js — Felgen-Kompatibilität (pures Matcher-Modul).
 *
 * Plan: docs/superpowers/plans/2026-06-10-felgen-kompatibilitaet.md
 * Spec: docs/superpowers/specs/2026-06-08-felgen-kompatibilitaet-design.md
 *
 * KEIN DOM. Läuft im Browser (window.TrautisWheelFitment, konsumiert von
 * product-wheel-compat-trautis.js) und in Node (scripts/wheel-fitment/*).
 *
 * Eingaben:
 *   vehicle — Eintrag aus theme/assets/trautis-vehicle-wheel-specs.json
 *   specs   — { lochkreis, lochanzahl, et_offset, felgenbreite_zoll,
 *               felgendurchmesser_zoll, zentrierung_mm } als STRINGS
 *               ('' = fehlt), wie im PDP-Produkt-JSON (main-product Z.53-58).
 *
 * Status-Semantik: siehe Plan-Abschnitt "Status-Semantik". unknown niemals
 * automatisch zu no-match eskalieren.
 */
(function (root) {
  'use strict';

  var EPS = 0.05; // Dezimal-Vergleiche (72.6 mm, 8.5″) — float-sicher

  // "5 X 120" / "5×112" → "5x120"; nicht parsebar → null
  function normalizeBoltPattern(raw) {
    if (raw === null || raw === undefined) return null;
    var s = String(raw).toLowerCase().replace(/×/g, 'x').replace(/\s+/g, '').replace(/,/g, '.');
    var m = s.match(/^(\d+)x(\d+(?:\.\d+)?)$/);
    if (!m) return null;
    return m[1] + 'x' + m[2];
  }

  function boltCount(pattern) {
    var p = normalizeBoltPattern(pattern);
    if (!p) return null;
    return parseInt(p.split('x')[0], 10);
  }

  // "72,6" | "72.6" | 72.6 → 72.6; '' / null / Müll → null. "0" → 0 (ET 0 gültig!)
  function toNum(raw) {
    if (raw === null || raw === undefined || raw === '') return null;
    if (typeof raw === 'number') return isFinite(raw) ? raw : null;
    var n = parseFloat(String(raw).replace(',', '.'));
    return isFinite(n) ? n : null;
  }

  function numEq(a, b) {
    return a !== null && b !== null && Math.abs(a - b) < EPS;
  }

  function note(notes, text) {
    if (notes.indexOf(text) === -1) notes.push(text);
  }

  function fmtMm(n) { return String(n).replace('.', ','); }

  // productRules (optional, Phase 2): produktspezifische Gutachten-Regeln als
  // { [chassis_code]: rules[] } — haben Vorrang vor vehicle.rules (Auswahl in
  // resolveRules). v1-Aufrufer übergeben nichts.
  // → { status: 'match'|'conditional'|'no-match'|'unknown', notes: ['…'] }
  function matchVariant(vehicle, specs, productRules) {
    var notes = [];
    if (!vehicle) return { status: 'unknown', notes: ['Für dieses Fahrzeug sind keine Felgendaten hinterlegt.'] };
    specs = specs || {};

    var vehiclePattern = normalizeBoltPattern(vehicle.bolt_pattern);
    var vehicleBore = toNum(vehicle.center_bore_mm);
    if (!vehiclePattern || vehicleBore === null) {
      return { status: 'unknown', notes: ['Fahrzeugdaten unvollständig.'] };
    }

    var wheelPattern = normalizeBoltPattern(specs.lochkreis);
    var wheelBore = toNum(specs.zentrierung_mm);
    if (!wheelPattern || wheelBore === null) {
      return { status: 'unknown', notes: ['Felgendaten unvollständig — Passung bitte anfragen.'] };
    }

    // Harte Gates (Design §4 Schritt 2)
    if (wheelPattern !== vehiclePattern) {
      return { status: 'no-match', notes: ['Lochkreis ' + wheelPattern + ' passt nicht zu ' + vehiclePattern + '.'] };
    }
    var wheelHoles = toNum(specs.lochanzahl);
    if (wheelHoles === null) wheelHoles = boltCount(wheelPattern);
    var vehicleHoles = boltCount(vehiclePattern);
    if (wheelHoles !== null && vehicleHoles !== null && wheelHoles !== vehicleHoles) {
      return { status: 'no-match', notes: ['Lochzahl ' + wheelHoles + ' passt nicht (Fahrzeug: ' + vehicleHoles + ').'] };
    }
    if (wheelBore < vehicleBore - EPS) {
      return { status: 'no-match', notes: ['Nabenbohrung ' + fmtMm(wheelBore) + ' mm ist kleiner als die Fahrzeugnabe (' + fmtMm(vehicleBore) + ' mm).'] };
    }
    var ringNeeded = wheelBore > vehicleBore + EPS;
    if (ringNeeded) note(notes, 'Zentrierring ' + fmtMm(wheelBore) + ' → ' + fmtMm(vehicleBore) + ' mm erforderlich.');

    return resolveRules(vehicle, specs, ringNeeded, notes, productRules);
  }

  // Größenregeln (Design §4 Schritt 4): Regeln mit gleicher Breite+Durchmesser
  // suchen; ET muss in mindestens einer liegen. Keine Größenregel → unknown.
  // Größenregel vorhanden, ET überall außerhalb → no-match (bekannte Regel
  // schließt aus). ringNeeded stuft match auf conditional ab.
  function resolveRules(vehicle, specs, ringNeeded, notes, productRules) {
    var width = toNum(specs.felgenbreite_zoll);
    var diameter = toNum(specs.felgendurchmesser_zoll);
    var et = toNum(specs.et_offset);
    if (width === null || diameter === null || et === null) {
      note(notes, 'Felgen-Größenangaben unvollständig — Passung bitte anfragen.');
      return { status: 'unknown', notes: notes };
    }

    // Regelquelle: produktspezifische Gutachten-Regeln (Phase 2) schlagen die
    // generischen Fahrzeug-Regeln. Ist productRules gesetzt, aber das Chassis
    // nicht gelistet, gilt LEERE Liste → unknown (das Gutachten dieses Rads
    // deckt das Fahrzeug nicht — NICHT auf vehicle.rules zurückfallen).
    var rules;
    if (productRules) {
      rules = Array.isArray(productRules[vehicle.chassis_code]) ? productRules[vehicle.chassis_code] : [];
    } else {
      rules = Array.isArray(vehicle.rules) ? vehicle.rules : [];
    }
    var sized = [];
    for (var i = 0; i < rules.length; i++) {
      if (numEq(toNum(rules[i].width_zoll), width) && numEq(toNum(rules[i].diameter_zoll), diameter)) {
        sized.push(rules[i]);
      }
    }
    if (sized.length === 0) {
      note(notes, 'Für diese Größe ist noch keine geprüfte Freigabe hinterlegt.');
      return { status: 'unknown', notes: notes };
    }

    var inEt = [];
    for (var j = 0; j < sized.length; j++) {
      var lo = toNum(sized[j].et_min);
      var hi = toNum(sized[j].et_max);
      if ((lo === null || et >= lo - EPS) && (hi === null || et <= hi + EPS)) inEt.push(sized[j]);
    }
    if (inEt.length === 0) {
      note(notes, 'ET ' + et + ' liegt außerhalb der freigegebenen Bereiche für diese Größe.');
      return { status: 'no-match', notes: notes };
    }

    var best = 'conditional';
    for (var k = 0; k < inEt.length; k++) {
      if (inEt[k].status === 'match') best = 'match';
    }
    for (var n = 0; n < inEt.length; n++) {
      if (inEt[n].note) note(notes, inEt[n].note);
      if (inEt[n].axle === 'front') note(notes, 'Freigabe gilt für die Vorderachse.');
      if (inEt[n].axle === 'rear') note(notes, 'Freigabe gilt für die Hinterachse.');
    }
    if (best === 'match' && ringNeeded) best = 'conditional';
    return { status: best, notes: notes };
  }

  var STATUS_RANK = { 'match': 0, 'conditional': 1, 'unknown': 2, 'no-match': 3 };

  // Alle Fahrzeuge der DB gegen EINE Varianten-Spec → sortierte Tabellen-Zeilen.
  function listVehicleMatches(db, specs, productRules) {
    var vehicles = (db && db.vehicles) || [];
    var out = [];
    for (var i = 0; i < vehicles.length; i++) {
      var res = matchVariant(vehicles[i], specs, productRules);
      out.push({
        chassis_code: vehicles[i].chassis_code,
        model_label: vehicles[i].model_label || '',
        status: res.status,
        notes: res.notes,
      });
    }
    out.sort(function (a, b) {
      var d = STATUS_RANK[a.status] - STATUS_RANK[b.status];
      if (d !== 0) return d;
      return a.chassis_code < b.chassis_code ? -1 : a.chassis_code > b.chassis_code ? 1 : 0;
    });
    return out;
  }

  var api = {
    matchVariant: matchVariant,
    listVehicleMatches: listVehicleMatches,
    STATUS_RANK: STATUS_RANK,
    normalizeBoltPattern: normalizeBoltPattern,
    boltCount: boltCount,
    toNum: toNum,
    numEq: numEq,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
  if (typeof window !== 'undefined') {
    window.TrautisWheelFitment = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
